// Package Imports
import { message as antdMessage, notification } from 'antd'
import type { ArgsProps } from 'antd/es/notification'

type ToastInput =
  | string
  | (Partial<ArgsProps> & { message: ArgsProps['message'] })

const DEDUPE_MS = 2500
const lastShown: Record<string, number> = {}

const dedupe = (key: string): boolean => {
  const now = Date.now()
  if (now - (lastShown[key] ?? 0) < DEDUPE_MS) return false
  lastShown[key] = now
  return true
}

const normalize = (
  input: ToastInput
): Partial<ArgsProps> & { message: ArgsProps['message'] } =>
  typeof input === 'string' ? { message: input } : input

const show = (
  variant: 'success' | 'error' | 'info' | 'warning',
  input: ToastInput
) => {
  const args = normalize(input)
  const key = `notify:${variant}:${String(args.message)}:${String(args.description ?? '')}`
  if (!dedupe(key)) return
  notification[variant]({
    placement: 'topRight',
    duration: 3,
    ...args,
  })
}

const flashShow = (
  variant: 'success' | 'error' | 'info' | 'warning',
  content: string
) => {
  const key = `flash:${variant}:${content}`
  if (!dedupe(key)) return
  antdMessage[variant](content)
}

export const notify = {
  success: (input: ToastInput) => show('success', input),
  error: (input: ToastInput) => show('error', input),
  info: (input: ToastInput) => show('info', input),
  warning: (input: ToastInput) => show('warning', input),
}

/**
 * Inline transient banner (antd `message`). Use for ephemeral feedback that
 * doesn't merit a full toast (validation hints, small confirmations).
 * For richer payloads (titles + descriptions) use `notify.*`.
 */
export const flash = {
  success: (content: string) => flashShow('success', content),
  error: (content: string) => flashShow('error', content),
  info: (content: string) => flashShow('info', content),
  warning: (content: string) => flashShow('warning', content),
}
